import api from './api';
import axios from "axios";

export type IndicadoresResponse = {
  dolar: number | null;
  ipcaServicos: number | null;
  ipcaAcumulado?: number | null;
  selic: number | null;
  totalMunicipios: number;
  totalEmpreendimentos: number;
  totalUnidades: number;
  totalIncorporadoras: number;
  vso: number;
};

const BCB_BASE = "https://api.bcb.gov.br/dados/serie/bcdata.sgs";

/**
 * Pega todos os dados de uma série entre datas, ou últimos valor
 */
async function fetchBCBSerie(
  serie: number,
  dataInicial?: string,
  dataFinal?: string
): Promise<{ data: string; valor: string }[] | null> {
  try {
    let url = `${BCB_BASE}.${serie}/dados?formato=json`;
    if (dataInicial && dataFinal) {
      url += `&dataInicial=${encodeURIComponent(dataInicial)}&dataFinal=${encodeURIComponent(dataFinal)}`;
    }
    const resp = await axios.get<{ data: string; valor: string }[]>(url, { timeout: 8000 });
    return resp.data;
  } catch (err: any) {
    console.error(`Erro ao buscar série ${serie}:`, err.message ?? err);
    return null;
  }
}

/**
 * Pega o último valor de uma série do BCB (genérico)
 */
async function fetchUltimoValorBCB(serie: number): Promise<number | null> {
  const dados = await fetchBCBSerie(serie);
  if (!dados || dados.length === 0) return null;
  const ultimo = dados[dados.length - 1];
  const valor = ultimo.valor.replace(",", ".");
  const num = Number(valor);
  return isNaN(num) ? null : num;
}

/**
 * Pega o último valor da SELIC via nova API
 */
async function fetchUltimoSelic(): Promise<number | null> {
  try {
    const url = "https://api.bcb.gov.br/dados/serie/bcdata.sgs.432/dados/ultimos/1?formato=json";
    const resp = await axios.get<{ data: string; valor: string }[]>(url, { timeout: 8000 });
    const valorStr = resp.data?.[0]?.valor;
    const num = Number(valorStr?.replace(",", "."));
    return isNaN(num) ? null : num;
  } catch (err: any) {
    console.error("Erro ao buscar SELIC:", err.message ?? err);
    return null;
  }
}

/**
 * Busca o último valor do IPCA acumulado (IBGE API)
 */
export async function fetchUltimoIPCAAcumulado(): Promise<number | null> {
  try {
    const url =
      "https://servicodados.ibge.gov.br/api/v3/agregados/7060/periodos/-6/variaveis/2265?localidades=N1[all]&classificacao=315[7169]";
    const resp = await axios.get(url, { timeout: 8000 });

    const series = resp.data?.[0]?.resultados?.[0]?.series?.[0]?.serie;
    if (!series) return null;

    const keys = Object.keys(series).sort();
    const ultimaChave = keys[keys.length - 1];
    const valorStr = series[ultimaChave];
    const valor = Number(valorStr?.replace(",", "."));

    return isNaN(valor) ? null : valor;
  } catch (err: any) {
    console.error("Erro ao buscar IPCA acumulado:", err.message ?? err);
    return null;
  }
}

export async function getIndicadores(): Promise<IndicadoresResponse> {
  const SERIE_IPCA_SERVICOS = 10844; // mensal BCB

  const ipcaServicos = await fetchUltimoValorBCB(SERIE_IPCA_SERVICOS);
  const ipcaAcumulado = await fetchUltimoIPCAAcumulado();
  const selic = await fetchUltimoSelic(); // <- usa a nova API

  // Dólar
  let dolar: number | null = null;
  try {
    const { data } = await axios.get("https://economia.awesomeapi.com.br/json/last/USD-BRL");
    const obj = data?.USDBRL;
    if (obj?.bid) {
      dolar = Number(obj.bid.replace(",", "."));
    }
  } catch (err: any) {
    console.error("Erro na API de câmbio USD-BRL:", err.message ?? err);
  }

  // API Indicadores secundários
  let totalMunicipios = 0;
  let totalEmpreendimentos = 0;
  let totalUnidades = 0;
  let totalIncorporadoras = 0;
  let vso = 0;

  try {
    const { data } = await api.get("/indicadores/");

    if (data) {
      totalMunicipios = data.total_municipios ?? 0;
      totalEmpreendimentos = data.total_empreendimentos ?? 0;
      totalUnidades = data.total_unidades ?? 0;
      totalIncorporadoras = data.total_incorporadoras ?? 0;
      vso = data.vso ?? 0;
    }
  } catch (err: any) {
    console.error("Erro ao buscar indicadores secundários:", err.message ?? err);
  }

  return {
    dolar,
    ipcaServicos,
    ipcaAcumulado,
    selic,
    totalMunicipios,
    totalEmpreendimentos,
    totalUnidades,
    totalIncorporadoras,
    vso,
  };
}
